import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { ButtonHTMLAttributes, Ref } from 'react'

import { cn } from '@/shared/lib'

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center gap-1 whitespace-nowrap',
    'cursor-pointer rounded-md transition-colors',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    'after:absolute after:inset-0 after:left-0 after:h-px after:w-full after:transition-all',
    'focus-visible:ring-2 focus-visible:ring-gray-200 focus-visible:outline-none',
    'disabled:pointer-events-none',
  ],

  {
    variants: {
      color: {
        black: 'text-black hover:after:bg-black',
        gray: 'text-gray-500 hover:after:bg-gray-500',
        purple:
          'text-purple-500 hover:after:bg-purple-500 focus-visible:ring-purple-200 active:text-purple-500',
        blue: 'text-blue-500 hover:after:bg-blue-500 focus-visible:ring-blue-200 active:text-blue-500',
      },
      size: {
        small: ['typo-caption1 min-h-4', 'after:top-[calc(100%-2.5px)]'],
        large: ['typo-label1 min-h-5', 'after:top-[calc(100%-4px)]'],
      },
      underline: {
        true: '',
        false: '',
      },
      weight: {
        bold: 'font-bold',
        medium: 'font-medium',
        normal: 'font-normal',
      },
    },

    compoundVariants: [
      {
        color: 'black',
        underline: true,
        className: 'after:bg-black',
      },
      {
        color: 'gray',
        underline: true,
        className: 'after:bg-gray-500',
      },
      {
        color: 'purple',
        underline: true,
        className: 'after:bg-purple-500',
      },
      {
        color: 'blue',
        underline: true,
        className: 'after:bg-blue-500',
      },
    ],
  },
)

export type Props = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    ref?: Ref<HTMLButtonElement>
    asChild?: boolean
  }

export const TextButton = ({
  className,
  color = 'black',
  size = 'large',
  underline = false,
  weight = 'medium',
  asChild = false,
  disabled = false,
  ...props
}: Props) => {
  const Component = asChild ? Slot : 'button'

  return (
    <Component
      className={cn(
        buttonVariants({
          color,
          size,
          underline,
          weight,
          className,
        }),
      )}
      disabled={disabled}
      {...props}
    />
  )
}
